import userDetail from "Stores/userDetail";
import { linkedInReplyState } from "../state";
import PaywallUtils from "Utils/PaywallUtils";
import { makeApi, URLS } from "Utils/apiURL";

const linkedInUtils = {};

const POPUP_POLL_INTERVAL_MS = 1000;
const POPUP_POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Unipile redirects the popup back to our own app (liConnectionPageUrl) on
// both success and cancel. LinkedInAccounts/main.js detects it's running
// inside that popup (window.opener set) and posts LINKEDIN_OAUTH_POPUP_DONE
// before closing itself - postMessage works regardless of origin, unlike
// reading popup.location, which is blocked cross-origin (e.g. when the
// popup's return URL doesn't match the opener's own origin, as in local dev
// against a staging backend). The closed-poll below is only a fallback for
// the plain "user closed the popup manually" case.
const openOAuthPopupAndAwaitClose = popup => {
  return new Promise(resolve => {
    if (!popup) {
      resolve();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearInterval(intervalId);
      window.removeEventListener("message", onMessage);
      if (!popup.closed) {
        popup.close();
      }
      resolve();
    };

    const onMessage = event => {
      if (event?.data?.type === "LINKEDIN_OAUTH_POPUP_DONE") {
        finish();
      }
    };
    window.addEventListener("message", onMessage);

    const startedAt = Date.now();
    const intervalId = setInterval(() => {
      const timedOut = Date.now() - startedAt > POPUP_POLL_TIMEOUT_MS;
      if (popup.closed || timedOut) {
        finish();
      }
    }, POPUP_POLL_INTERVAL_MS);
  });
};

// Connects (or reconnects, when accountId is provided) a LinkedIn account via
// the Unipile-hosted OAuth flow. Account type (standard vs. Sales Navigator)
// is detected by the backend during connect - no user choice needed here.
linkedInUtils.connectViaOAuth = async (accountId = null) => {
  const {
    planName = "",
    inFreePlan = false,
    liAutomationEnabled = false,
    noOfLiAccountsUsed = 0,
    noOfLiAccountsEligible = 0
  } = userDetail?.userFeatureFlag || {};

  await linkedInReplyState.getCurrentTeam();
  if (!userDetail?.userFeatureFlag?.liAutomationEnabled) {
    PaywallUtils.upgradeUtilsFunction("linkedInAutomationNotEnabled", false);
    return;
  }
  if (!liAutomationEnabled) {
    return;
  }
  if (!accountId && noOfLiAccountsUsed >= noOfLiAccountsEligible) {
    PaywallUtils.upgradeUtilsFunction(
      "multipleLinkedInCustom",
      false,
      planName,
      inFreePlan
    );
    return;
  }

  linkedInReplyState.setLinkedInConnectionLoading(true);
  try {
    const config = accountId
      ? {
          url: `${URLS.getLinkedInOAuthReconnectUrl}?accountId=${accountId}`,
          method: "GET"
        }
      : { url: URLS.getLinkedInOAuthConnectUrl, method: "GET" };
    const res = await makeApi(config);
    const authUrl = res?.data?.authUrl || res?.authUrl;
    if (!authUrl) {
      linkedInReplyState.setLinkedInConnectionLoading(false);
      linkedInReplyState.setLinkedInMsg(
        "Unable to start the LinkedIn connection. Please try again."
      );
      linkedInReplyState.setShowLinkedInNotConnectedPopup(true);
      return;
    }
    const popup = window.open(
      authUrl,
      "linkedInOAuthConnect",
      "width=500,height=700"
    );
    await openOAuthPopupAndAwaitClose(popup);
    linkedInReplyState.setLinkedInConnectionLoading(false);
    linkedInReplyState.getLinkedInAccounts();
  } catch (e) {
    linkedInReplyState.setLinkedInConnectionLoading(false);
    linkedInReplyState.setLinkedInMsg(
      "Something went wrong while connecting your LinkedIn account."
    );
    linkedInReplyState.setShowLinkedInNotConnectedPopup(true);
  }
};

linkedInUtils.handleSalesNavigatorButtonClick = async () => {
  await linkedInUtils.connectViaOAuth();
};

linkedInUtils.handleButtonClick = async () => {
  await linkedInUtils.connectViaOAuth();
};

export { linkedInUtils };
export default linkedInUtils;
